import {
  AccountingBackend,
  getDeleteAfterPushLabel,
  getEditAndPushLabel,
  getEditedBeforePushLabel,
  getPushButtonLabel,
  getPushingWithoutAttachmentLabel,
  getPushWithoutAttachmentLabel,
  getStoredAccountingBackend,
  getUpdateAfterPushLabel,
} from "../../../config/accounting";

/** Human-readable labels for review action codes (snake_case), backend-aware. */
export function getReviewActionLabels(
  backend: AccountingBackend = getStoredAccountingBackend(),
): Record<string, string> {
  return {
    create: "Create",
    override_gl_permanent: "Permanent GL Override",
    override_gl_once: "One-Time GL Override",
    update: "Update",
    update_after_push: getUpdateAfterPushLabel(backend),
    push_to_zoho: getPushButtonLabel(backend),
    push_to_zoho_without_attachment: getPushingWithoutAttachmentLabel(backend),
    delete_after_push_to_zoho: getDeleteAfterPushLabel(backend),
    edit: getEditedBeforePushLabel(backend),
    approve: "Approve",
    advance: "Advance",
    expense_high_value: "Expense High Value",
    split_transaction: "Split Transaction",
    split_transaction_amount: "Split Transaction Amount",
    unmatch: "Unmatch",
    create_recurring: "Create Recurring",
    cancel_recurring_before_end: "Cancel Recurring Before End Date",
    ap_ar_create: "AP/AR Journal – Create",
    ap_ar_delete: "AP/AR Journal – Delete",
    reject: "Reject",
    duplicate: "Mark as Duplicate",
    write_off: "Write off",
    cancel_write_off: "Cancel write off",
    month_close: "Post-close change",
    bulk_upload: "Bulk upload",
    mark_as_done: "Mark as Done",
    to_be_done_later: "To be done later",
  };
}

/** @deprecated Use getReviewActionLabels(backend) for backend-aware labels. */
export const REVIEW_ACTION_LABELS: Record<string, string> =
  getReviewActionLabels("zoho");

/** Post-close change (month_close) is always shown first when present. */
export function sortActionTypesForDisplay(actionTypes: string[]): string[] {
  return [...actionTypes].sort((a, b) => {
    if (a === "month_close") return -1;
    if (b === "month_close") return 1;
    return 0;
  });
}

export function formatActionTypeLabel(
  actionCode: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string {
  return getReviewActionLabels(backend)[actionCode] || actionCode;
}

export function formatActionTypesList(
  actionTypes?: string[],
  fallbackActionCode?: string,
  backend: AccountingBackend = getStoredAccountingBackend(),
): string[] {
  const codes =
    actionTypes && actionTypes.length > 0
      ? sortActionTypesForDisplay(actionTypes)
      : fallbackActionCode
        ? [fallbackActionCode]
        : [];
  return codes.map((code) => formatActionTypeLabel(code, backend));
}

/** Format action_types for banners/cards; merges backend-aware push labels with extras. */
export function formatCombinedActionTypes(
  actionTypes: string[],
  backend: AccountingBackend = getStoredAccountingBackend(),
  extraLabels: Record<string, string> = {},
  formatFallback: (value: string) => string = (value) => value,
): string {
  const actionLabels = { ...getReviewActionLabels(backend), ...extraLabels };
  const sorted = sortActionTypesForDisplay(actionTypes);
  const filteredActionTypes = sorted.filter((action) => {
    if (action === "edit" && actionTypes.includes("push_to_zoho")) {
      return false;
    }
    return true;
  });
  const hasEditAndPush =
    actionTypes.includes("edit") && actionTypes.includes("push_to_zoho");
  if (hasEditAndPush) {
    return getEditAndPushLabel(backend);
  }
  return filteredActionTypes
    .map((action) => actionLabels[action] || formatFallback(action))
    .join(" + ");
}

export {
  getPushWithoutAttachmentLabel,
  getEditAndPushLabel,
} from "../../../config/accounting";
